import { useNavigate } from 'react-router-dom';
import {
  Shield, ShieldCheck, ScanSearch, Scale, Globe, ArrowRight,
  CheckCircle, Zap, Lock, FileText, QrCode, BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from '@/i18n/TranslationContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { cn } from '@/lib/utils';

const featureIcons = [ScanSearch, ShieldCheck, Scale, QrCode, BarChart3, Lock];
const featureKeys = ['feature_multi_law', 'feature_deterministic', 'feature_fines', 'feature_qr', 'feature_reports', 'feature_rbac'];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useTranslation();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span className="text-lg font-bold tracking-tight text-foreground">VRS</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-login">{t('log_in')}</Button>
            <Button onClick={() => navigate('/register')} data-testid="landing-signup">{t('sign_up')}</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 md:py-32 px-6 vrs-grid">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(600px circle at 50% 20%, rgba(26,209,230,0.08), transparent 55%)' }} />
        <div className="max-w-4xl mx-auto text-center relative">
          <Badge variant="outline" className="text-xs mb-6 border-primary/30 text-primary">{t('landing_badge')}</Badge>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground leading-tight">
            {t('landing_hero')}{' '}<span className="text-primary">{t('landing_hero_accent')}</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-2xl mx-auto leading-relaxed">
            {t('landing_desc')}
          </p>
          <div className="flex items-center justify-center gap-4 mt-10">
            <Button size="lg" className="h-12 px-8 text-base font-semibold" onClick={() => navigate('/register')} data-testid="landing-get-started">
              {t('get_started_free')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-8 text-base" onClick={() => navigate('/login')}>
              {t('log_in')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/60 mt-6 italic">
            {t('landing_disclaimer')}
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 px-6 border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-center text-foreground mb-12">{t('landing_features_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureKeys.map((key, i) => {
              const Icon = featureIcons[i];
              return (
                <Card key={key} className="border-border/60 hover:border-border/80 transition-[border-color] duration-200">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{t(key)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(key + '_desc')}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-border/30">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('landing_cta_title')}</h2>
          <p className="text-muted-foreground">{t('landing_cta_desc')}</p>
          <Button size="lg" className="h-12 px-8 text-base font-semibold" onClick={() => navigate('/register')}>
            <Zap className="mr-2 h-5 w-5" />{t('start_scanning')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">{t('powered_by')} {"\u2014"} {t('cybercheck_product')}</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">{t('privacy_policy')}</a>
            <a href="#" className="hover:text-foreground transition-colors">{t('terms_of_service')}</a>
            <a href="https://www.cybercheck.one" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">{t('contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
